import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from './ui/table';
import { Button } from './ui/button';
import { TablePeriodSelector } from './ui/TablePeriodSelector';
import { TableFilterBar } from './ui/TableFilterBar';
import { TableExportMenu } from './ui/TableExportMenu';
import { Label } from './ui/label';
import { useAppData } from '../hooks/useAppData';
import { matchesMonthYear, matchesYear } from '../utils/dateFilters';
import * as XLSX from 'xlsx';
import * as FileSaver from 'file-saver';

type CuotaResumen = {
  vendedorId: string;
  vendedorNombre: string;
  comprador: string;
  lotes: string[];
  recibo: string;
  cuotas: { cuota: number; fecha: string; monto: number; pagado: number; saldo: number }[]; // Solo del período
  cuotasTodas: { cuota: number; fecha: string; monto: number; pagado: number; saldo: number; estado: string }[]; // Todas las cuotas
  totalMonto: number;
  totalPagado: number;
  totalSaldo: number;
  saldoDelPeriodo: number; // Saldo independiente del filtro (vencidas + próxima)
  pagoSugerido: number; // Suma de vencidas + próxima
  cuotasCount: number; // Número de cuotas para mostrar en la columna
  fechaPago: string; // Fecha de vencimiento de las cuotas del período
};

function formatGT(dateStr: string) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('es-GT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export default function ReporteCobrosMensuales() {
  const { lots, expenses, sellers } = useAppData();

  // Función robusta para obtener abonos sincronizados (idéntica a PaymentSystem)
  function getAbonosSincronizadosRobusto(lot: any, expenses: any[]): any[] {
    if (!lot.payments) return [];
    return lot.payments.filter((p: any) => {
      if (p.type !== 'abono') return false;
      return expenses.some((e: any) => {
        let lotesExpense = e.lotes;
        if (typeof lotesExpense === 'string') lotesExpense = [lotesExpense];
        if (!Array.isArray(lotesExpense)) lotesExpense = [];
        const montoIgual = Math.abs(Number(e.monto) - Number(p.amount)) < 1;
        const tipoIngreso = e.tipo === 'ingreso';
        const fechaPago = new Date(p.date);
        const fechaExpense = new Date(e.fecha);
        const diffDias = Math.abs((fechaExpense.getTime() - fechaPago.getTime()) / (1000 * 60 * 60 * 24));
        const fechaIgual = diffDias <= 1;
        const referenciaIncluye = (e.referencia && (p.cuotaNumero ? e.referencia.includes(p.cuotaNumero) : false)) || (e.referencia && p.receipt && e.referencia.includes(p.receipt));
        const loteCoincide = lotesExpense.includes(lot.id?.toString());
        return montoIgual && tipoIngreso && fechaIgual && referenciaIncluye && loteCoincide;
      });
    });
  }
  const months = ['Todos', 'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const currentMonthName = months[new Date().getMonth() + 1];
  const [selectedMonth, setSelectedMonth] = React.useState<string>(currentMonthName);
  const monthIndex = (m: string) => months.indexOf(m) - 1; // 'Todos' => -1
  const [lastMonthIdx, setLastMonthIdx] = React.useState<number>(monthIndex(currentMonthName));
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = React.useState<number>(currentYear);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [selectedSeller, setSelectedSeller] = React.useState<string>('Todos');
  const [selectedEstado, setSelectedEstado] = React.useState<string>('Todos');
  const [openRows, setOpenRows] = React.useState<Set<number>>(() => new Set());
  const maxCuotas = React.useMemo(() => {
    try {
      const arr = lots
        .filter((l: any) => l.status === 'crédito' && l.paymentType === 'pagos')
        .map((l: any) => l.numberOfPayments || l.numeroPagos || 0);
      const m = Math.max(0, ...arr);
      return m > 0 ? m : 12;
    } catch {
      return 12;
    }
  }, [lots]);
  const [selectedCuota, setSelectedCuota] = React.useState<string>('Todas');

  // Construir cuotas por grupo de venta y calcular pagos
  const data: CuotaResumen[] = React.useMemo(() => {
    // Agrupar lotes a crédito por grupo (vendedor|comprador|DPI|fecha|recibo)
    const grupos: Record<string, any[]> = {};
    lots.filter((l: any) => l.status === 'crédito' && l.paymentType === 'pagos').forEach((l: any) => {
      const key = `${l.seller}|${l.buyerName}|${l.buyerDPI}|${l.saleDate}|${l.receiptNumber}`;
      if (!grupos[key]) grupos[key] = [];
      grupos[key].push(l);
    });

    const result: CuotaResumen[] = [];
    // Año de referencia seleccionado
    const refYear = selectedYear;
    const selIdx = selectedMonth === 'Todos' ? 11 : (months.indexOf(selectedMonth) - 1);
    const selStart = selectedMonth === 'Todos' ? new Date(refYear, 0, 1) : new Date(refYear, selIdx, 1);
    const selEnd = selectedMonth === 'Todos' ? new Date(refYear, 11, 31) : new Date(refYear, selIdx + 1, 0); // último día del mes
    const today = new Date();
    const todayMid = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    Object.values(grupos).forEach((grupo) => {
      if (!grupo || grupo.length === 0) return;
      const comprador = grupo[0].buyerName;
      const vendedor = grupo[0].seller;
      const vendedorObj = sellers.find((s: any) => s.id === vendedor || s.account === vendedor || s.name === vendedor);
      const vendedorNombre = vendedorObj ? vendedorObj.name : (vendedor || '-');
      if (selectedSeller !== 'Todos' && vendedorNombre !== selectedSeller) return;
      const lotesTmp = grupo.map((l: any) => l.id?.toString());
      const recibo = grupo[0].receiptNumber;
      const lotes = lotesTmp;
      const totalValue = grupo.reduce((sum: number, l: any) => sum + (Number(l.totalValue) || 0), 0);
      // USAR FUNCIÓN ROBUSTA PARA OBTENER ABONOS SINCRONIZADOS
      const abonosSincronizados = grupo.flatMap((l: any) => getAbonosSincronizadosRobusto(l, expenses));
      const payments = grupo.flatMap((l: any) => Array.isArray(l.payments) ? l.payments : []);
      const numberOfPayments = grupo[0].numberOfPayments || grupo[0].numeroPagos || 1;
      const enganche = payments.find((p: any) => p.type === 'enganche') ? Number(payments.find((p: any) => p.type === 'enganche').amount) : 0;
      const cuotaMensual = numberOfPayments > 0 ? (totalValue - enganche) / numberOfPayments : 0;
      const fechaVenta = new Date(grupo[0].saleDate || new Date());

      // Generar todas las cuotas del grupo
      const cuotasTodas: { cuota: number; fecha: string; monto: number; pagado: number; saldo: number; estado: string }[] = [];
      for (let i = 1; i <= numberOfPayments; i++) {
        const fechaVenc = new Date(fechaVenta);
        fechaVenc.setMonth(fechaVenc.getMonth() + i);
        const fechaStr = fechaVenc.toISOString().split('T')[0];
        // CALCULAR PAGOS USANDO ABONOS SINCRONIZADOS (incluye manuales y automáticos)
        const pagosCuota = abonosSincronizados.filter((p: any) => {
          const sameCuota = String(p.cuotaNumero) === String(i);
          // Tolerar pequeñas diferencias de fecha de vencimiento
          const pVenc = (p.cuotaFechaVencimiento || '').toString().slice(0, 10);
          const sameVenc = !p.cuotaFechaVencimiento || pVenc === fechaStr;
          return sameCuota && sameVenc;
        });
        const pagado = pagosCuota.reduce((s: number, p: any) => s + Number(p.amount || 0), 0);
        const saldo = Math.max(cuotaMensual - pagado, 0);

        // ESTA LÓGICA ANTIGUA SE MUEVE DESPUÉS DEL LOOP - AHORA FILTRAREMOS AL FINAL

        // Arreglo completo de cuotas (independiente del filtro del mes)
        let estadoDet = 'Pendiente';
        if (saldo <= 0.0001) {
          estadoDet = 'Pagada';
        } else if (fechaVenc < todayMid) {
          estadoDet = 'Vencida';
        }
        // La lógica de "Próxima" se determinará después del loop
        cuotasTodas.push({ cuota: i, fecha: fechaStr, monto: cuotaMensual, pagado, saldo, estado: estadoDet });
      }

      // Determinar cuál es la "Próxima" cuota: primera cuota no pagada con fecha >= hoy
      let proximaAsignada = false;
      cuotasTodas.forEach(cuota => {
        if (!proximaAsignada && cuota.saldo > 0.0001 && new Date(cuota.fecha) >= todayMid) {
          cuota.estado = 'Próxima';
          proximaAsignada = true;
        }
      });

      // Calcular saldo del período (independiente del filtro): vencidas + próxima
      const cuotasVencidas = cuotasTodas.filter(c => c.estado === 'Vencida');
      const cuotaProxima = cuotasTodas.find(c => c.estado === 'Próxima');
      const saldoDelPeriodo = cuotasVencidas.reduce((sum, c) => sum + c.saldo, 0) + (cuotaProxima ? cuotaProxima.saldo : 0);

      // LÓGICA CORREGIDA: MOSTRAR INFORMACIÓN DE LAS CUOTAS DEL MES SELECCIONADO
      const cuotasFiltradas: { cuota: number; fecha: string; monto: number; pagado: number; saldo: number }[] = [];

      if (selectedMonth === 'Todos') {
        // Mostrar solo cuotas pendientes (no pagadas) de CUALQUIER año
        cuotasTodas.forEach(cuotaCompleta => {
          if (cuotaCompleta.saldo > 0.0001) {
            // Filtro por número de cuota  
            const pasaCuota = selectedCuota === 'Todas' || cuotaCompleta.cuota === Number(selectedCuota);
            // Filtro por estado
            const pasaEstado = selectedEstado === 'Todos' || selectedEstado === cuotaCompleta.estado;

            if (pasaCuota && pasaEstado) {
              cuotasFiltradas.push({
                cuota: cuotaCompleta.cuota,
                fecha: cuotaCompleta.fecha,
                monto: cuotaCompleta.saldo, // Solo saldo pendiente
                pagado: cuotaCompleta.pagado,
                saldo: cuotaCompleta.saldo
              });
            }
          }
        });
      } else {
        // Para un mes específico: mostrar cuotas que vencen en ese mes (CUALQUIER AÑO)
        // INDEPENDIENTEMENTE de si están pagadas o no (para el conteo correcto)
        cuotasTodas.forEach(cuotaCompleta => {
          const fechaVencimiento = new Date(cuotaCompleta.fecha);
          const mesVencimiento = fechaVencimiento.getMonth();

          const esMesSeleccionado = mesVencimiento === selIdx;

          if (esMesSeleccionado) {
            // Filtro por número de cuota  
            const pasaCuota = selectedCuota === 'Todas' || cuotaCompleta.cuota === Number(selectedCuota);
            // Filtro por estado
            const pasaEstado = selectedEstado === 'Todos' || selectedEstado === cuotaCompleta.estado;

            if (pasaCuota && pasaEstado) {
              cuotasFiltradas.push({
                cuota: cuotaCompleta.cuota,
                fecha: cuotaCompleta.fecha,
                monto: cuotaCompleta.monto, // Monto original de la cuota
                pagado: cuotaCompleta.pagado,
                saldo: cuotaCompleta.saldo
              });
            }
          }
        });

        // NO agregar cuotas adicionales - solo las que vencen específicamente en este mes
      }

      // Si el filtro de estado es distinto de 'Todos' y no hay cuotas en el período que coincidan, omitimos.
      if (selectedEstado !== 'Todos' && cuotasFiltradas.length === 0) {
        return;
      }

      // El saldo del período YA ES el que debe mostrarse (vencidas + próxima)
      const pagoSugerido = 0; // No mostrar pago sugerido separado para evitar confusión

      // Incluir el grupo si tiene cuotas que coinciden con los filtros O si se muestran todos los estados
      if (cuotasFiltradas.length > 0 || selectedEstado === 'Todos') {
        // CALCULAR MÉTRICAS CORRECTAS PARA EL MES SELECCIONADO
        let totalMonto = 0;
        let totalPagado = 0;
        let totalSaldo = 0;
        let cuotasCount = 0;

        if (selectedMonth === 'Todos') {
          // Para "Todos": solo cuotas pendientes
          totalMonto = cuotasFiltradas.reduce((s, c) => s + c.monto, 0);
          totalPagado = cuotasFiltradas.reduce((s, c) => s + c.pagado, 0);
          totalSaldo = cuotasFiltradas.reduce((s, c) => s + c.saldo, 0);
          cuotasCount = cuotasFiltradas.filter(c => c.saldo > 0.0001).length;
        } else {
          // Para un mes específico: 
          // - MONTOS: de todas las cuotas del mes (pagadas + pendientes)
          // - CONTEO: solo cuotas pendientes
          const cuotasPendientesDelMes = cuotasFiltradas.filter(c => c.saldo > 0.0001);

          totalMonto = cuotasFiltradas.reduce((s, c) => s + c.monto, 0);
          totalPagado = cuotasFiltradas.reduce((s, c) => s + c.pagado, 0);
          totalSaldo = cuotasFiltradas.reduce((s, c) => s + c.saldo, 0);

          // Conteo: solo cuotas pendientes del mes
          cuotasCount = cuotasPendientesDelMes.length;
        }

        // Buscar fecha de la cuota que vence en el mes seleccionado (del detalle)
        let fechaPago = '';
        if (selectedMonth === 'Todos') {
          // Para "Todos": usar la primera cuota pendiente
          const cuotaPendiente = cuotasTodas.find(c => c.saldo > 0.0001);
          fechaPago = cuotaPendiente ? cuotaPendiente.fecha : (cuotasTodas[0]?.fecha || '');
        } else {
          // Para mes específico: buscar cuota que vence en ese mes (cualquier año)
          const selIdx = months.indexOf(selectedMonth) - 1;
          const cuotaDelMes = cuotasTodas.find(c => {
            const fechaCuota = new Date(c.fecha);
            return fechaCuota.getMonth() === selIdx;
          });
          fechaPago = cuotaDelMes ? cuotaDelMes.fecha : '';
        }

        const resultItem = {
          vendedorId: vendedorObj?.id || vendedor,
          vendedorNombre,
          comprador,
          lotes,
          recibo,
          cuotas: cuotasFiltradas, // Usar las cuotas filtradas
          cuotasTodas,
          totalMonto,
          totalPagado,
          totalSaldo,
          saldoDelPeriodo, // Saldo independiente del filtro
          pagoSugerido, // Mantener por compatibilidad pero será 0
          cuotasCount, // Para mostrar en la columna "Cuotas"
          fechaPago // Fecha de vencimiento de las cuotas del período
        };

        result.push(resultItem);
      }
    });
    return result.sort((a, b) => a.vendedorNombre.localeCompare(b.vendedorNombre) || a.comprador.localeCompare(b.comprador));
  }, [lots, expenses, sellers, selectedMonth, selectedYear, selectedSeller, selectedEstado, selectedCuota, months]);

  const exportGeneralCSV = () => {
    const rows = data.flatMap(r => r.cuotas.map(c => ({
      'Vendedor': r.vendedorNombre,
      'Comprador': r.comprador,
      'Lotes': r.lotes.join(', '),
      'Recibo': r.recibo,
      'Cuota': c.cuota,
      'Fecha Vencimiento': c.fecha,
      'Fecha Pago Período': r.fechaPago,
      'Monto Cuota': c.monto,
      'Pagado': c.pagado,
      'Saldo': c.saldo,
    })));
    const ws = XLSX.utils.json_to_sheet(rows);
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const mes = selectedMonth === 'Todos' ? 'todos' : selectedMonth;
    FileSaver.saveAs(blob, `reporte-cobros-${mes}.csv`);
  };

  const exportPorVendedorCSV = () => {
    const mes = selectedMonth === 'Todos' ? 'todos' : selectedMonth;
    data.forEach(r => {
      const rows = r.cuotas.map(c => ({
        'Comprador': r.comprador,
        'Lotes': r.lotes.join(', '),
        'Recibo': r.recibo,
        'Cuota': c.cuota,
        'Fecha Vencimiento': c.fecha,
        'Monto Cuota': c.monto,
        'Pagado': c.pagado,
        'Saldo': c.saldo,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      FileSaver.saveAs(blob, `cobros-${r.vendedorNombre.replace(/\s+/g, '_')}-${mes}.csv`);
    });
  };

  const totalSaldoGeneral = data.reduce((s, r) => s + r.totalSaldo, 0);
  const totalMontoGeneral = data.reduce((s, r) => s + r.totalMonto, 0);
  const totalPagadoGeneral = data.reduce((s, r) => s + r.totalPagado, 0);

  return (
    <div className="space-y-6">
      {/* Encabezado fuera de contenedores */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">Reportes</h2>
          <p className="text-muted-foreground mt-1 text-[13px]">Selecciona filtros y período para ver los cobros del mes</p>
        </div>
      </div>

      {/* Filtros y contenido */}
      <div className="space-y-6">
        {/* Filtros de Búsqueda - Horizontal */}
        <div className="flex flex-wrap items-center gap-3 mb-6 px-1">
          {/* Vendedor */}
          <div className="relative">
            <select value={selectedSeller} onChange={e => setSelectedSeller(e.target.value)} className="bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pl-3 pr-8 appearance-none cursor-pointer min-w-[150px]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8F98' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '10px' }}>
              <option>Todos</option>
              {sellers.map((s: any) => (
                <option key={s.id} value={s.name}>{s.name}</option>
              ))}
            </select>
          </div>
          {/* Estado */}
          <div className="relative">
            <select value={selectedEstado} onChange={e => setSelectedEstado(e.target.value)} className="bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pl-3 pr-8 appearance-none cursor-pointer min-w-[120px]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8F98' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '10px' }}>
              <option>Todos</option>
              <option>Pagada</option>
              <option>Próxima</option>
              <option>Vencida</option>
              <option>Pendiente</option>
            </select>
          </div>
          {/* Cuota */}
          <div className="relative">
            <select value={selectedCuota} onChange={e => setSelectedCuota(e.target.value)} className="bg-transparent border border-input hover:border-ring focus:border-ring rounded-[6px] h-8 text-[13px] text-foreground shadow-none transition-colors pl-3 pr-8 appearance-none cursor-pointer min-w-[100px]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%238A8F98' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 0.5rem center', backgroundSize: '10px' }}>
              <option>Todas</option>
              {Array.from({ length: maxCuotas }, (_, i) => i + 1).map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>

          <div className="flex-1" />

          <TableExportMenu onCSV={exportGeneralCSV} onExcel={() => { }} onPDF={() => { }} />
        </div>

        {/* Período de Consulta - sin card wrapper */}
        <TablePeriodSelector
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          onMonthChange={(m) => {
            setSelectedMonth(m);
            setLastMonthIdx(monthIndex(m));
          }}
          onYearChange={(y) => {
            setSelectedYear(y);
          }}
          months={months}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          <Card className="bg-card border border-border shadow-none rounded-lg overflow-hidden">
            <CardHeader className="pb-2"><CardTitle className="text-[13px] font-medium text-muted-foreground">Total esperado</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold text-foreground">Q. {totalMontoGeneral.toLocaleString(undefined, { minimumFractionDigits: 2 })}</CardContent>
          </Card>
          <Card className="bg-card border border-emerald-500/20 shadow-none rounded-lg overflow-hidden">
            <CardHeader className="pb-2"><CardTitle className="text-[13px] font-medium text-muted-foreground">Total pagado</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold text-emerald-500">Q. {totalPagadoGeneral.toLocaleString(undefined, { minimumFractionDigits: 2 })}</CardContent>
          </Card>
          <Card className="bg-card border border-amber-500/20 shadow-none rounded-lg overflow-hidden">
            <CardHeader className="pb-2"><CardTitle className="text-[13px] font-medium text-muted-foreground">Total pendiente</CardTitle></CardHeader>
            <CardContent className="text-2xl font-bold text-amber-500">Q. {totalSaldoGeneral.toLocaleString(undefined, { minimumFractionDigits: 2 })}</CardContent>
          </Card>
        </div>
        {/* Tabla en Card independiente */}
        <Card className="bg-card border-border shadow-none rounded-lg overflow-hidden">
          <CardHeader className="pb-3 pt-4 px-4 border-b border-border/50">
            <div className="flex justify-between items-center">
              <CardTitle className="text-sm font-medium tracking-tight text-foreground">Cobros ({data.length})</CardTitle>
              <span className="text-xs text-muted-foreground">Mostrando {data.length} registros filtrados</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="hover:bg-transparent border-b border-border/50">
                    <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Vendedor</TableHead>
                    <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Comprador</TableHead>
                    <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Lotes</TableHead>
                    <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Cuotas ({selectedMonth})</TableHead>
                    <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Fecha de Pago</TableHead>
                    <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Monto del período</TableHead>
                    <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Pagado {selectedMonth !== 'Todos' ? 'del período' : ''}</TableHead>
                    <TableHead className="px-3 py-2 text-[11px] font-medium text-muted-foreground h-10">Saldo del período</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map((r, idx) => (
                    <React.Fragment key={idx}>
                      <TableRow className="border-b border-border/50 hover:bg-muted/50 transition-colors">
                        <TableCell className="text-foreground">{r.vendedorNombre}</TableCell>
                        <TableCell className="text-foreground">{r.comprador}</TableCell>
                        <TableCell className="font-mono text-sm text-foreground">{r.lotes.join(', ')}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-foreground">{r.cuotasCount} cuota(s)</span>
                            <Button
                              variant="outline"
                              className="h-7 px-2 text-xs border-input hover:bg-accent text-foreground"
                              onClick={() => {
                                setOpenRows(prev => {
                                  const next = new Set(prev);
                                  if (next.has(idx)) next.delete(idx); else next.add(idx);
                                  return next;
                                });
                              }}
                            >{openRows.has(idx) ? 'Ocultar' : 'Ver detalle'}</Button>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm text-blue-500">
                          {formatGT(r.fechaPago)}
                        </TableCell>
                        <TableCell className="font-mono text-foreground">Q. {r.totalMonto.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className="font-mono text-emerald-500">Q. {r.totalPagado.toLocaleString(undefined, { minimumFractionDigits: 2 })}</TableCell>
                        <TableCell className="font-mono text-amber-500">
                          Q. {r.totalSaldo.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                      </TableRow>
                      {openRows.has(idx) && (
                        <TableRow className="bg-muted/20">
                          <TableCell colSpan={8}>
                            <div className="text-sm text-foreground space-y-1 p-2">
                              {r.cuotasTodas.map((c, i2) => (
                                <div key={i2}>
                                  <span className="font-medium">#{c.cuota}</span> — {formatGT(c.fecha)} — Q. {(c.estado === 'Pagada' ? c.monto : c.saldo).toLocaleString(undefined, { minimumFractionDigits: 0 })}
                                  {(() => {
                                    const saldo = c.saldo;
                                    const cls = c.estado === 'Pagada' ? 'text-emerald-500' :
                                      c.estado === 'Vencida' ? 'text-red-500' :
                                        c.estado === 'Próxima' ? 'text-blue-500' :
                                          'text-muted-foreground'; // Pendiente en gris
                                    let label = '';
                                    if (c.estado === 'Pagada') {
                                      label = 'Pagada';
                                    } else if (c.estado === 'Vencida') {
                                      label = `Vencida (Pendiente Q. ${saldo.toLocaleString(undefined, { minimumFractionDigits: 0 })}${(c.pagado || 0) > 0 ? `, Pagado Q. ${(c.pagado || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}` : ''})`;
                                    } else if (c.estado === 'Próxima') {
                                      label = `Próxima (Pendiente Q. ${saldo.toLocaleString(undefined, { minimumFractionDigits: 0 })}${(c.pagado || 0) > 0 ? `, Pagado Q. ${(c.pagado || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}` : ''})`;
                                    } else {
                                      label = `Pendiente Q. ${saldo.toLocaleString(undefined, { minimumFractionDigits: 0 })}${(c.pagado || 0) > 0 ? ` (Pagado Q. ${(c.pagado || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })})` : ''}`;
                                    }
                                    return <span className={`ml-2 ${cls}`}>{label}</span>;
                                  })()}
                                </div>
                              ))}
                            </div>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                  {data.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground">No hay cuotas para el período seleccionado</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
